import { endOfDay, isValid, startOfDay } from 'date-fns';
import { ReportFilterConfig } from 'modules/reports/types';
import { useCallback, useMemo, useState } from 'react';

export type ReportDateRange = [string | null, string | null];
export type ReportFilterValue = string[] | ReportDateRange;
export type ReportFilterValues = Record<string, ReportFilterValue | undefined>;

const isActive = (value: ReportFilterValue | undefined): boolean =>
  Boolean(value && value.some(Boolean));

const toFilterValues = (
  value: string | string[] | Date | null | undefined
): string[] => {
  if (Array.isArray(value)) return value.map(String);
  return [value === null || value === undefined ? '' : String(value)];
};

/**
 * Client-side filtering over a fully loaded report dataset.
 * Multiselect options come from config.options (DB/enum-backed lists, so
 * dropdowns are usable before generating) merged with the distinct values
 * present in the loaded data.
 */
export const useReportFilters = <T>(
  data: T[] | null,
  configs: ReportFilterConfig<T>[],
  context?: unknown,
  defaultValues?: ReportFilterValues
) => {
  const [values, setValues] = useState<ReportFilterValues>(defaultValues ?? {});

  const setFilterValue = useCallback(
    (key: string, value: ReportFilterValue | undefined) => {
      setValues((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const clearFilters = useCallback(() => setValues({}), []);

  const optionsByKey = useMemo(() => {
    const result: Record<string, string[]> = {};
    configs.forEach((config) => {
      if (config.type !== 'multiselect') return;
      const distinct = new Set<string>(config.options ?? []);
      (data ?? []).forEach((item) => {
        toFilterValues(config.getValue(item, context)).forEach((value) => {
          if (value !== '') {
            distinct.add(value);
          }
        });
      });
      result[config.key] = [...distinct].sort((a, b) => a.localeCompare(b));
    });
    return result;
  }, [data, configs, context]);

  const filteredData = useMemo(() => {
    if (!data) return data;
    const activeConfigs = configs.filter((config) =>
      isActive(values[config.key])
    );
    if (activeConfigs.length === 0) return data;

    return data.filter((item) =>
      activeConfigs.every((config) => {
        const value = values[config.key] as ReportFilterValue;

        if (config.type === 'multiselect') {
          const selected = value as string[];
          const matches = toFilterValues(config.getValue(item, context)).some(
            (itemValue) => selected.includes(itemValue)
          );
          return config.exclude ? !matches : matches;
        }

        const [from, to] = value as ReportDateRange;
        const raw = config.getValue(item, context);
        if (!raw) return false;
        const date = new Date(raw as string | Date);
        if (!isValid(date)) return false;
        if (from && date < startOfDay(new Date(from))) return false;
        if (to && date > endOfDay(new Date(to))) return false;
        return true;
      })
    );
  }, [data, configs, values, context]);

  const hasActiveFilters = useMemo(
    () => Object.values(values).some(isActive),
    [values]
  );

  return {
    values,
    setFilterValue,
    clearFilters,
    optionsByKey,
    filteredData,
    hasActiveFilters,
  };
};
